/**
 * Authentication. These are the only calls that run without a session, so they use
 * `fetch` directly rather than going through the refresh-aware wrapper.
 */

import type { ApiClient } from '../core/client'
import { errorFrom } from '../core/client'
import type { StoredSession } from '../core/session'
import type { AuthResponse, VerificationRequiredResponse } from '../types/profile'

function sessionFrom(body: AuthResponse): StoredSession {
  return {
    accessToken: body.access_token,
    refreshToken: body.refresh_token ?? null,
    userId: body.user_id,
    ...(body.expires_at ? { expiresAt: body.expires_at } : {}),
  }
}

export class AuthResource {
  constructor(private readonly client: ApiClient) {}

  private async postPublic<T>(path: string, body: unknown): Promise<T> {
    const response = await fetch(this.client.url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) throw await errorFrom(response)
    return (await response.json()) as T
  }

  /** Signs in and persists the session. Email is normalised here so a stray capital
   *  in a typed address is never the reason a sign-in fails. */
  async login(email: string, password: string): Promise<StoredSession> {
    const body = await this.postPublic<AuthResponse>('/auth/login', {
      email: email.trim().toLowerCase(),
      password,
    })
    const session = sessionFrom(body)
    this.client.store.save(session)
    return session
  }

  /**
   * Creates an account.
   *
   * Returns either a live session or a "confirm your email first" marker, depending
   * on the project's settings — so callers must discriminate with
   * `isVerificationRequired` before assuming they are signed in.
   */
  async signUp(
    email: string,
    password: string,
    displayName?: string,
  ): Promise<StoredSession | VerificationRequiredResponse> {
    // Registration is deliberately distinct from `/auth/login` in the live API.
    // Keeping this explicit avoids a silent 404 when a new student signs up.
    const body = await this.postPublic<AuthResponse | VerificationRequiredResponse>('/auth/register', {
      email: email.trim().toLowerCase(),
      password,
      ...(displayName ? { display_name: displayName } : {}),
    })

    if ('email_verification_required' in body && body.email_verification_required) {
      return body
    }

    const session = sessionFrom(body as AuthResponse)
    this.client.store.save(session)
    return session
  }

  /** Sends a reset link. Always resolves — telling a caller whether an address is
   *  registered would turn this into an account-enumeration oracle. */
  async requestPasswordReset(email: string): Promise<void> {
    await this.postPublic<unknown>('/auth/password-reset', {
      email: email.trim().toLowerCase(),
    })
  }

  /** Clears the local session first: an offline sign-out is still a sign-out, and
   *  leaving the token behind because the network failed is the wrong failure mode. */
  async logout(): Promise<void> {
    const session = this.client.session
    this.client.store.clear()
    if (!session) return

    try {
      await fetch(this.client.url('/auth/logout'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.accessToken}` },
      })
    } catch {
      /* best effort */
    }
  }
}
